import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { alpha } from '@material-ui/core';
import clsx from 'clsx';
import PropTypes from 'prop-types';

// Components
import { Mui } from 'components';
import * as DisplayData from 'components/common/DisplayData';
import Tooltip from 'components/common/Tooltip';

const isNumericAmount = amount => {
  if (typeof amount === 'number') return Number.isFinite(amount);
  if (typeof amount !== 'string') return false;
  const trimmed = amount.trim();
  return trimmed !== '' && Number.isFinite(Number(trimmed));
};

const useStyles = Mui.makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    boxShadow: theme.shadows[1],
    transition: 'border-color 0.2s, background-color 0.2s, box-shadow 0.2s',
  },
  clickable: {
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
      borderColor: theme.palette.primary.main,
      boxShadow: theme.shadows[2],
    },
  },
  selected: {
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
    boxShadow: theme.shadows[2],
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
      borderColor: theme.palette.primary.main,
    },
  },
  button: {
    width: '100%',
    height: '100%',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  content: {
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 110,
    boxSizing: 'border-box',
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    minWidth: 0,
    minHeight: 42,
  },
  headerIcon: {
    fontSize: 16,
    marginRight: theme.spacing(0.75),
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    color: ({ selected, color }) =>
      selected
        ? theme.palette.primary.main
        : theme.palette[color]?.main || theme.palette.text.secondary,
    '& svg': {
      width: 16,
      height: 16,
    },
  },
  title: {
    fontWeight: 600,
    letterSpacing: 0.3,
    lineHeight: 1.25,
    fontSize: '0.6875rem',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    wordBreak: 'normal',
    overflowWrap: 'normal',
    hyphens: 'none',
    flex: 1,
    minWidth: 0,
    color: ({ selected }) => (selected ? theme.palette.primary.main : theme.palette.text.secondary),
  },
  info: {
    flexShrink: 0,
    marginLeft: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
  },
  quantity: {
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    '& .MuiTypography-body2': {
      fontWeight: 400,
      fontSize: theme.typography.body2.fontSize,
      color: theme.palette.text.secondary,
    },
  },
  footer: {
    paddingTop: theme.spacing(0.5),
  },
  amount: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  extra: {
    marginTop: theme.spacing(0.5),
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    '& .MuiTypography-root': {
      color: theme.palette.text.secondary,
    },
  },
}));

const stopInfoEvent = event => {
  event.preventDefault();
  event.stopPropagation();
};

const renderAmount = amount => {
  if (amount === undefined || amount === null) return null;
  if (isNumericAmount(amount)) {
    return <DisplayData.CurrencyField value={amount} />;
  }
  return amount;
};

const renderHeaderIcon = (icon, className) => {
  if (!icon) return null;
  if (typeof icon === 'string') {
    return <Mui.Icon className={className}>{icon}</Mui.Icon>;
  }
  return <Mui.Box className={className}>{icon}</Mui.Box>;
};

const renderInfo = (info, className) => {
  if (!info) return null;
  const content =
    typeof info === 'string' ? (
      <Tooltip title={null} description={info} iconProps={{ fontSize: 'small' }} />
    ) : (
      info
    );

  return (
    <Mui.Box
      className={className}
      onClick={stopInfoEvent}
      onMouseDown={stopInfoEvent}
      onKeyDown={stopInfoEvent}>
      {content}
    </Mui.Box>
  );
};

function CardComponent(props) {
  const {
    title,
    icon,
    color = 'primary',
    quantity,
    amount,
    value,
    info,
    children,
    selected = false,
    onClick,
    to,
    className,
    style,
  } = props;

  // Hooks
  const classes = useStyles({ selected, color });

  // Constants
  const hasQuantity = quantity !== undefined && quantity !== null;
  const hasValue = value !== undefined && value !== null;
  const hasAmount = amount !== undefined && amount !== null;
  const isClickable = !!onClick || !!to;

  const renderMainValue = () => {
    let content = null;
    if (hasValue) content = value;
    else if (hasQuantity) content = <DisplayData.NumberField value={quantity} />;
    else if (hasAmount) content = renderAmount(amount);
    if (content === null) return null;

    return (
      <Mui.Typography variant="h4" className={classes.quantity} color="textPrimary" component="div">
        {content}
      </Mui.Typography>
    );
  };

  const content = (
    <Mui.Box className={classes.content}>
      <Mui.Box className={classes.header}>
        {renderHeaderIcon(icon, classes.headerIcon)}
        <Mui.Tooltip title={typeof title === 'string' ? title : ''} placement="top" arrow>
          <Mui.Typography
            variant="overline"
            className={classes.title}
            component="span"
            display="block">
            {title}
          </Mui.Typography>
        </Mui.Tooltip>
        {renderInfo(info, classes.info)}
      </Mui.Box>
      {renderMainValue()}
      <Mui.Box className={classes.footer}>
        {hasQuantity && hasAmount && (
          <Mui.Typography variant="body2" className={classes.amount} component="div">
            {renderAmount(amount)}
          </Mui.Typography>
        )}
        {!!children && (
          <Mui.Box className={classes.extra}>
            {typeof children === 'string' ? (
              <Mui.Typography variant="body2" color="textSecondary">
                {children}
              </Mui.Typography>
            ) : (
              children
            )}
          </Mui.Box>
        )}
      </Mui.Box>
    </Mui.Box>
  );

  const actionProps = {
    className: classes.button,
    ...(to ? { component: RouterLink, to } : {}),
    ...(onClick ? { onClick } : {}),
  };

  return (
    <Mui.Card
      className={clsx(classes.root, className, {
        [classes.selected]: selected,
        [classes.clickable]: isClickable,
      })}
      style={style}
      variant="outlined">
      {isClickable ? (
        <Mui.CardActionArea {...actionProps}>{content}</Mui.CardActionArea>
      ) : (
        <Mui.Box className={classes.button}>{content}</Mui.Box>
      )}
    </Mui.Card>
  );
}

CardComponent.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'info',
    'warning',
    'error',
    'success',
    'disabled',
  ]),
  quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.node]),
  value: PropTypes.any,
  info: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  to: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default CardComponent;
